import { MotionGlobalConfig } from 'framer-motion'

/**
 * Suppresses Framer Motion animations for the duration of a
 * `document.startViewTransition()`.
 *
 * ## Why
 *
 * View Transitions capture a paint snapshot of the new page AFTER the
 * React commit but BEFORE `useEffect` runs. Framer Motion's mount-time
 * animations are kicked off from `useEffect`, so at snapshot time every
 * `motion.div` with `initial: { opacity: 0 }` is still painted at
 * opacity 0. The snapshot then cross-fades from opacity 0 to 1 — which,
 * since the captured pixels are opacity 0, looks like a fade from blank
 * to blank. When the snapshot is torn down, Motion's animations finally
 * fire (or are mid-flight), and the real DOM is briefly visible at
 * partial opacity. That's the "elements flash on and off at the end"
 * bug reported in Safari (and present in any browser; Safari just makes
 * it more visible because its snapshot teardown is a hard cut).
 *
 * ## How
 *
 * We monkey-patch `document.startViewTransition` exactly once. Inside
 * the patched function we set `MotionGlobalConfig.skipAnimations = true`
 * SYNCHRONOUSLY before invoking the real implementation. With that flag
 * set, Motion skips the transition and applies the `animate` state
 * directly — the snapshot captures the page at opacity 1. After the
 * transition's `finished` promise settles we restore the flag so normal
 * mount animations work again on subsequent navigations (and refreshes
 * are unaffected because they don't go through `startViewTransition`).
 *
 * Must be invoked before any React-Router-driven view transition can
 * fire. The right place is the client entry point.
 */

let installed = false

export function installViewTransitionMotionGuard() {
	if (installed) return
	if (typeof document === 'undefined') return
	if (typeof document.startViewTransition !== 'function') return

	const original = document.startViewTransition.bind(document)
	document.startViewTransition = function patchedStartViewTransition(
		...args: Parameters<Document['startViewTransition']>
	) {
		MotionGlobalConfig.skipAnimations = true
		const transition = original(...args)
		// Restore on both finished and ready completion paths.
		const restore = () => {
			MotionGlobalConfig.skipAnimations = false
		}
		transition.finished.then(restore, restore)
		return transition
	}

	installed = true
}
